@if (myself) {
  <section class="user-settings-details-wrapper" [attr.aria-labelledby]="'details-heading'">
    <app-cr-header id="details-heading" [headingLevel]="2">
      {{ 'USER_SETTINGS.USER_SETTINGS_DETAILS' | translate }}
    </app-cr-header>
    <div class="form-container">
      <!-- Avatar -->
      <div class="spinner-container">
        <nz-spin [nzSpinning]="loading$ | async" [attr.aria-busy]="loading$ | async">
          @if (userName) {
            <app-cr-avatar
              [userName]="userName"
              [theme]="'THEME-2'"
              [size]="'L'"
              [attr.aria-hidden]="true"></app-cr-avatar>
          }
        </nz-spin>
      </div>
      <p id="details-form-description" class="sr-only" [attr.aria-hidden]="true">
        {{ 'USER_SETTINGS.USER_SETTINGS_DETAILS_DESCRIPTION' | translate }}
      </p>
      <form
        nz-form
        nzLayout="vertical"
        class="details-form"
        [formGroup]="form"
        [attr.aria-describedby]="'details-form-description'">
        <!-- E-MAIL - Always disabled and never part of the form payload -->
        <nz-form-item>
          <nz-form-label nzRequired nzFor="email">
            {{ 'COMMON.LABEL.EMAIL' | translate }}
          </nz-form-label>
          <nz-form-control>
            <input nz-input [disabled]="true" id="email" type="email" formControlName="email" required />
          </nz-form-control>
        </nz-form-item>
        <!-- USERNAME -->
        <nz-form-item>
          <nz-form-label nzRequired nzFor="name">
            {{ 'COMMON.LABEL.USERNAME' | translate }}
          </nz-form-label>
          <nz-form-control nzHasFeedback [nzValidateStatus]="form.controls.name" [nzErrorTip]="nameErrorTpl">
            <input
              nz-input
              id="name"
              type="text"
              formControlName="name"
              required
              [attr.aria-invalid]="form.controls.name.invalid && form.controls.name.touched"
              [attr.aria-describedby]="form.controls.name.invalid && form.controls.name.touched ? 'nameError' : null" />
          </nz-form-control>
          <ng-template #nameErrorTpl>
            <mat-error id="nameError">
              {{ form.controls.name.errors | errorsFormatingAndTranslate }}
            </mat-error>
          </ng-template>
        </nz-form-item>
        <!-- LANGUAGE -->
        @if (languages$ | async; as languages) {
          <nz-form-item>
            <nz-form-label id="languageLabel" nzRequired nzFor="language">
              {{ 'COMMON.LANGUAGE.LAYOUT_LANGUAGE' | translate }}
            </nz-form-label>
            <nz-form-control
              nzHasFeedback
              [nzValidateStatus]="form.controls.languageId"
              [nzErrorTip]="languageErrorTpl">
              <nz-select
                id="language"
                formControlName="languageId"
                [nzPlaceHolder]="'COMMON.LANGUAGE.LAYOUT_LANGUAGE' | translate"
                [required]="true"
                [nzShowSearch]="false"
                [attr.aria-labelledby]="'languageLabel'"
                [attr.aria-invalid]="
                  form.controls.languageId.invalid && form.controls.languageId.touched ? 'true' : null
                "
                [attr.aria-describedby]="
                  form.controls.languageId.invalid && form.controls.languageId.touched ? 'languageError' : null
                ">
                @for (lang of languages; track lang) {
                  <nz-option [nzValue]="lang.id" [nzLabel]="'COMMON.LANGUAGE.' + lang.key.toUpperCase() | translate">
                  </nz-option>
                }
              </nz-select>
            </nz-form-control>
            <!-- ERROR -->
            <ng-template #languageErrorTpl>
              <mat-error id="languageError">
                {{ form.controls.languageId.errors | errorsFormatingAndTranslate }}
              </mat-error>
            </ng-template>
          </nz-form-item>
        }

        <!-- PHONE NUMBER -->
        @if (countries$ | async; as countries) {
          <nz-form-item>
            <nz-form-label nzRequired nzFor="telephoneNumber">
              {{ 'COMMON.LABEL.PHONE_NUMBER' | translate }}
            </nz-form-label>
            <nz-form-control>
              <app-phone-input inputId="telephoneNumber" formControlName="telephoneNumber" [countries]="countries">
              </app-phone-input>
            </nz-form-control>
          </nz-form-item>
        }
        <nz-form-item class="mfa-form-item">
          <nz-form-control>
            <span
              class="mfa-button-tooltip-wrapper"
              nz-tooltip
              [nzTooltipTitle]="
                isMfaButtonDisabled && mfaButtonLabelKey === 'ENABLE_MFA_MODAL.ENABLE_ACTION'
                  ? ('ENABLE_MFA_MODAL.DISABLED_TOOLTIP' | translate)
                  : null
              ">
              <button
                class="submit-button mfa-button"
                [class.mfa-button--highlight]="isEnableMfaAction"
                [class.mfa-button--subtle]="!isEnableMfaAction"
                type="button"
                (click)="handleMfaAction()"
                nz-button
                [nzType]="isEnableMfaAction ? 'primary' : 'default'"
                [disabled]="isMfaButtonDisabled">
                {{ mfaButtonLabelKey | translate }}
              </button>
            </span>
          </nz-form-control>
        </nz-form-item>
        <!-- OFFICE -->
        @if (shouldShowOfficeField && (offices$ | async)?.length) {
          <nz-form-item>
            <nz-form-label nzRequired nzFor="officeId">
              {{ 'COMMON.LABEL.ASSIGNED_OFFICE' | translate }}
            </nz-form-label>
            <nz-form-control nzHasFeedback [nzValidateStatus]="form.controls.officeId" [nzErrorTip]="officeIdErrorTpl">
              @if (shouldShowOfficeField && (offices$ | async)?.length) {
                <nz-select
                  id="officeId"
                  formControlName="officeId"
                  [nzPlaceHolder]="'COMMON.LABEL.ASSIGNED_OFFICE' | translate"
                  [required]="true"
                  [attr.aria-invalid]="form.controls.officeId.invalid && form.controls.officeId.touched"
                  [attr.aria-describedby]="
                    form.controls.officeId.invalid && form.controls.officeId.touched ? 'officeIdError' : null
                  ">
                  @for (office of offices$ | async; track office) {
                    <nz-option [nzValue]="office.id" [nzLabel]="office.addressCity + ' (' + office.addressStreet + ')'">
                    </nz-option>
                  }
                </nz-select>
              }
            </nz-form-control>
            <!-- ERROR -->
            <ng-template #officeIdErrorTpl>
              <mat-error id="officeIdError">
                {{ form.controls.officeId.errors | errorsFormatingAndTranslate }}
              </mat-error>
            </ng-template>
          </nz-form-item>
        }
        <div class="submit-column">
          <button
            class="submit-button submit"
            type="submit"
            (click)="submitProfileForm()"
            nz-button
            nzType="primary"
            [nzLoading]="loading$ | async"
            [disabled]="form.invalid || form.pristine">
            {{ 'COMMON.ACTION.UPDATE' | translate }}
          </button>
          <button
            class="submit-button"
            type="button"
            (click)="exitUserSettings()"
            nz-button
            nzType="default"
            [nzLoading]="loading$ | async">
            {{ 'USER_SETTINGS.EXIT_SETTINGS' | translate }}
          </button>
          <button
            class="submit-button remove"
            type="button"
            (click)="deleteAccount()"
            nz-button
            nzType="primary"
            nzDanger
            [nzLoading]="loading$ | async">
            {{ 'COMMON.ACTION.REMOVE_ACCOUNT' | translate }}
          </button>
        </div>
      </form>
    </div>
  </section>
}
